import { addMonths, isSameDay, startOfDay, startOfMonth } from '@crm/shared';
import { Button, Spinner } from '@crm/ui';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useAvailability, useProfessionals } from '../api';

export interface SlotChoice {
  professionalId: string;
  professionalName: string;
  professionalImage: string | null;
  startAt: Date;
}

interface ProfessionalSlotPickerProps {
  durationMinutes: number;
  unitId?: string;
  /** Escolha atual - o passo e controlado por quem o usa. */
  value: SlotChoice | null;
  onChange: (choice: SlotChoice | null) => void;
  /** Hora sugerida quando ainda nao ha escolha (fim do servico anterior). */
  suggestedStart?: Date;
  /** Agendamento em edicao: os itens dele nao contam como ocupado. */
  ignoreAppointmentId?: string;
  /** Servicos ja lancados nesta comanda, que tambem ocupam o profissional. */
  busy?: { professionalId: string | null; startAt: Date; durationMinutes: number }[];
}

const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' });
const monthLabel = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });
const hour = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

const pad = (value: number) => String(value).padStart(2, '0');
/** Chave local do dia - a API devolve a disponibilidade nesse formato. */
const dateKey = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);

  return `${parts[0]?.[0] ?? ''}${parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : ''}`.toUpperCase();
}

/**
 * Escolha do profissional e do horario de um servico: cards, o mes inteiro e
 * os horarios do dia. O que nao da para marcar aparece como indisponivel.
 */
export function ProfessionalSlotPicker({
  durationMinutes,
  unitId,
  value,
  onChange,
  suggestedStart,
  ignoreAppointmentId,
  busy = [],
}: ProfessionalSlotPickerProps) {
  const reference = value?.startAt ?? suggestedStart ?? new Date();

  const [professionalId, setProfessionalId] = useState<string | undefined>(value?.professionalId);
  const [qualquer, setQualquer] = useState(false);
  const [month, setMonth] = useState(() => startOfMonth(reference));
  const [day, setDay] = useState(() => startOfDay(reference));
  const [slot, setSlot] = useState<string | undefined>(
    value?.startAt.toISOString() ?? suggestedStart?.toISOString(),
  );

  const professionals = useProfessionals(unitId);
  const slotsRef = useRef<HTMLElement | null>(null);

  const monthEnd = useMemo(() => new Date(month.getFullYear(), month.getMonth() + 1, 0), [month]);

  const availability = useAvailability({
    professionalId,
    anyProfessional: qualquer,
    unitId,
    from: month,
    to: monthEnd,
    durationMinutes,
    ignoreId: ignoreAppointmentId,
  });

  const byDate = useMemo(() => {
    const map = new Map<string, { start: string; available: boolean }[]>();
    for (const entry of availability.data?.days ?? []) map.set(entry.date, entry.slots);

    return map;
  }, [availability.data]);

  const days = useMemo(
    () =>
      Array.from(
        { length: monthEnd.getDate() },
        (_, index) => new Date(month.getFullYear(), month.getMonth(), index + 1),
      ),
    [month, monthEnd],
  );

  const taken = busy.filter((item) => item.professionalId === professionalId);

  const freeOfComanda = (start: string) => {
    const from = new Date(start).getTime();
    const to = from + durationMinutes * 60_000;

    return !taken.some((item) => {
      const other = item.startAt.getTime();

      return from < other + item.durationMinutes * 60_000 && to > other;
    });
  };

  const slotsOf = (date: Date) =>
    (byDate.get(dateKey(date)) ?? []).map((option) => ({
      ...option,
      available: option.available && freeOfComanda(option.start),
    }));

  const slots = slotsOf(day);
  const freeCount = slots.filter((option) => option.available).length;
  const chosen = professionals.data?.data.find((item) => item.id === professionalId);
  /** Sem resposta da API ainda, nada e marcado como indisponivel. */
  const ready = Boolean(professionalId) && !availability.isLoading && availability.isSuccess;
  const activeSlot = slots.some((option) => option.start === slot && option.available)
    ? slot
    : undefined;

  // Sem isto os horarios ficam abaixo da dobra e parece que nao ha horario.
  // biome-ignore lint/correctness/useExhaustiveDependencies: profissional, dia e carregamento sao o gatilho da rolagem, nao valores lidos aqui.
  useEffect(() => {
    if (!professionalId || availability.isLoading) return;

    slotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [professionalId, day, availability.isLoading]);

  // A escolha so sobe quando esta completa: profissional + horario livre.
  useEffect(() => {
    if (!chosen || !activeSlot) {
      if (value !== null) onChange(null);
      return;
    }

    if (value?.professionalId === chosen.id && value.startAt.toISOString() === activeSlot) return;

    onChange({
      professionalId: chosen.id,
      professionalName: chosen.name,
      professionalImage: chosen.image ?? null,
      startAt: new Date(activeSlot),
    });
  }, [chosen, activeSlot, value, onChange]);

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">Profissional</h3>

        {/* Quem nao tem preferencia nao deveria precisar escolher: o sistema
            aloca quem estiver livre e diz quem ficou. */}
        <button
          type="button"
          onClick={() => {
            setQualquer((atual) => !atual);
            setProfessionalId(undefined);
          }}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
            qualquer ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/60',
          )}
        >
          <Users className="h-4 w-4 shrink-0 text-primary" />
          <span className="font-medium">Qualquer profissional</span>
          <span className="truncate text-xs text-muted-foreground">
            quem estiver livre no horario
          </span>
        </button>

        {professionals.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner className="h-5 w-5 text-muted-foreground" />
          </div>
        ) : (
          // Dois por linha ja no celular: em coluna unica os profissionais
          // empurravam o calendario e os horarios para fora da dobra.
          <div
            className={cn(
              'grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4',
              qualquer ? 'pointer-events-none opacity-50' : '',
            )}
          >
            {professionals.data?.data.map((professional) => {
              const active = professional.id === professionalId;

              return (
                <button
                  key={professional.id}
                  type="button"
                  onClick={() => setProfessionalId(professional.id)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border p-2 text-left transition-colors sm:gap-3 sm:p-3',
                    active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/60',
                  )}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary sm:h-11 sm:w-11 sm:text-base">
                    {initials(professional.name)}
                  </span>
                  <span className="truncate text-sm font-medium sm:text-base">
                    {professional.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            aria-label="Mes anterior"
            className="rounded-full p-2 transition-colors hover:bg-muted"
            onClick={() => setMonth((current) => addMonths(current, -1))}
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
          <span className="min-w-48 text-center text-lg font-semibold text-foreground first-letter:uppercase sm:min-w-56 sm:text-xl">
            {monthLabel.format(month)}
          </span>
          <button
            type="button"
            aria-label="Proximo mes"
            className="rounded-full p-2 transition-colors hover:bg-muted"
            onClick={() => setMonth((current) => addMonths(current, 1))}
          >
            <ChevronRight className="h-7 w-7" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 sm:grid-cols-8 lg:grid-cols-16">
          {days.map((current) => {
            const free = slotsOf(current).some((option) => option.available);
            const active = isSameDay(current, day);
            const unavailable = ready && !free;

            return (
              <button
                key={current.toISOString()}
                type="button"
                disabled={unavailable}
                title={unavailable ? 'Sem horario disponivel' : undefined}
                onClick={() => setDay(current)}
                className={cn(
                  'flex h-14 w-full flex-col items-center justify-center rounded-lg border transition-colors sm:h-16',
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border hover:bg-muted/60',
                  unavailable &&
                    'cursor-not-allowed border-dashed bg-muted/50 text-muted-foreground line-through hover:bg-muted/50',
                )}
              >
                <span className="text-[11px] font-semibold uppercase">
                  {weekday.format(current).replace('.', '')}
                </span>
                <span className="text-lg font-semibold">{current.getDate()}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-3 scroll-mt-4" ref={slotsRef}>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">Horario</h3>
          {professionalId && !availability.isLoading ? (
            <span className="text-sm text-muted-foreground">
              {freeCount === 0
                ? 'nenhum horario livre neste dia'
                : `${freeCount} ${freeCount === 1 ? 'horario livre' : 'horarios livres'}`}
            </span>
          ) : null}
        </div>

        {!professionalId && !qualquer ? (
          <p className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-base text-muted-foreground">
            Escolha o profissional para ver os horarios.
          </p>
        ) : availability.isError ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-6 text-center">
            <p className="text-base text-destructive">Nao foi possivel carregar os horarios.</p>
            <Button variant="outline" onClick={() => availability.refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : availability.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner className="h-5 w-5 text-muted-foreground" />
          </div>
        ) : slots.every((option) => !option.available) ? (
          <p className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-base text-muted-foreground">
            {/* No proprio dia a lista costuma esvaziar porque o expediente ja
                passou - dizer isso evita a impressao de tela quebrada. */}
            {isSameDay(day, new Date())
              ? 'O expediente de hoje ja passou ou esta todo ocupado. Escolha outro dia.'
              : 'Nao ha horario disponivel neste dia.'}
          </p>
        ) : (
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-8 lg:grid-cols-12">
            {slots.map((option) => (
              <button
                key={option.start}
                type="button"
                disabled={!option.available}
                title={option.available ? undefined : 'Indisponivel'}
                onClick={() => setSlot(option.start)}
                className={cn(
                  'h-10 rounded-lg border text-sm font-medium transition-colors',
                  option.start === activeSlot
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border hover:bg-muted/60',
                  !option.available &&
                    'cursor-not-allowed border-dashed bg-muted/50 text-muted-foreground line-through hover:bg-muted/50',
                )}
              >
                {hour.format(new Date(option.start))}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
